import { Box, Stack, Button } from "@mui/material";
import { useState, useMemo } from "react";
import { useParams } from "react-router";
import { useTenant } from "hooks/settings";
import {
  useGetLatestEventExamplesQuery,
  Event,
} from "src/store/api/generatedApi";
import { VideoControlProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useAreaLabelledEvents } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { getEventLabel } from "src/utils/eventUtils";
import { calculateVideoWindow } from "./utils/videoWindowUtils";
import { LoadingState, EmptyState } from "./components/ExamplePlayerStates";
import { ExampleNavigationControls } from "./components/ExampleNavigationControls";
import { ExampleVideoPlayerContent } from "./components/ExampleVideoPlayerContent";

type EventExampleVideoPlayerProps = Readonly<{
  eventType: "lift" | "drop";
  buttonLabel: string;
}>;

export const EventExampleVideoPlayer = ({
  eventType,
  buttonLabel,
}: EventExampleVideoPlayerProps) => {
  const { location } = useParams<{ location: string }>();
  const withAreaLabels = useAreaLabelledEvents(location);
  const tenant = useTenant();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const { data: rawExamplesData, isLoading: isLoadingExamples } =
    useGetLatestEventExamplesQuery(
      {
        tenantName: tenant,
        location: location ?? "",
        eventType,
        limit: 10,
      },
      { skip: !location || !showVideo }
    );

  const examplesData = rawExamplesData;

  const currentExample = useMemo<Event | null>(() => {
    if (!examplesData?.events || examplesData.events.length === 0) {
      return null;
    }
    return examplesData.events[currentIndex] ?? null;
  }, [examplesData, currentIndex]);

  const videoWindow = useMemo(() => {
    if (!currentExample) return null;

    const eventStartTime = dayjs.utc(currentExample.data.start);
    const eventEndTime = dayjs.utc(currentExample.data.end);

    return calculateVideoWindow(eventStartTime, eventEndTime);
  }, [currentExample]);

  const events = useMemo<ScrapEyesEvent[]>(() => {
    if (!currentExample || !videoWindow) return [];

    const eventStartTime = dayjs.utc(currentExample.data.start);
    const eventEndTime = dayjs.utc(currentExample.data.end);

    const material = getEventLabel(currentExample.labels, "material");
    const area = getEventLabel(currentExample.labels, "area");

    return withAreaLabels([
      {
        id: String(currentExample.id),
        start: eventStartTime,
        end: eventEndTime,
        eventType: currentExample.data.event_type,
        material,
        area,
      },
    ]);
  }, [currentExample, videoWindow, withAreaLabels]);

  const handleNext = () => {
    if (examplesData?.events) {
      setCurrentIndex((prev) =>
        prev < examplesData.events.length - 1 ? prev + 1 : prev
      );
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  if (!showVideo) {
    return (
      <Box
        sx={{
          p: 3,
          bgcolor: "background.default",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            setShowVideo(true);
          }}
          disabled={!location}
        >
          {buttonLabel}
        </Button>
      </Box>
    );
  }

  if (isLoadingExamples) {
    return <LoadingState minHeight={200} />;
  }

  if (!examplesData || !currentExample || !videoWindow || !location) {
    return <EmptyState minHeight={200} />;
  }

  return (
    <Stack spacing={2}>
      <VideoControlProvider key={currentExample.id}>
        <ExampleVideoPlayerContent
          location={location}
          startIso={videoWindow.startIso}
          endIso={videoWindow.endIso}
          events={events}
          videoStart={videoWindow.videoStart}
        />
      </VideoControlProvider>
      <ExampleNavigationControls
        currentIndex={currentIndex}
        totalCount={examplesData.events.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </Stack>
  );
};
